import { HTMLInputTypeAttribute, ReactNode, useState } from "react";
import styled, { css } from "styled-components";
import COLORS from "components/atoms/colors";
import { InputChange } from "types/Form";

interface BorderColor {
  red: string;
  black: string;
  gray: string;
  darkGray: string;
}

type Shape = "round" | "square";

interface Props {
  children?: ReactNode;
  placeholder?: string;
  className?: {
    container?: string;
    input?: string;
  };
  error?: boolean;
  small?: boolean;
  value?: string;
  onChange?: (e: InputChange) => void;
  type?: HTMLInputTypeAttribute;
  name?: string;
  maxLength?: number;
  minLength?: number;
  readOnly?: boolean;
  shape: Shape;
}

const InputText = ({
  children,
  placeholder,
  className,
  error,
  small,
  value,
  type,
  onChange,
  name,
  minLength,
  maxLength,
  readOnly,
  shape,
}: Props) => {
  const [focus, setFocus] = useState(false);

  const borderColor = (() => {
    if (error) return "red";
    if (focus || value) return "black";
    return "darkGray";
  })();

  return (
    <Container
      className={className?.container}
      borderColor={borderColor}
      small={small}
      shape={shape}
    >
      <div className="flex-1 h-full flex items-center">
        <Input
          type={type || "text"}
          placeholder={placeholder}
          onFocus={() => !readOnly && setFocus(true)}
          onBlur={() => setFocus(false)}
          red={error}
          value={value}
          onChange={onChange}
          name={name}
          maxLength={maxLength}
          minLength={minLength}
          readOnly={readOnly}
          className={className?.input}
        />
      </div>
      {children && <div className="flex-none">{children}</div>}
    </Container>
  );
};

const Container = styled.div<{
  borderColor: keyof BorderColor;
  small?: boolean;
  shape: Shape;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  height: 44px;
  padding: 12px;
  border-width: 1px;
  border-color: ${({ borderColor }) =>
    ({
      red: COLORS.error.active,
      black: COLORS.black["100%"],
      gray: COLORS.black["20%"],
      darkGray: COLORS.black["40%"],
    }[borderColor])};
  background: #fff;

  border-radius: ${({ shape }) =>
    ({
      square: "4px",
      round: "44px",
    }[shape])};

  ${({ className }) =>
    !className?.includes("w-") &&
    css`
      width: 100%;
      flex: 1;
    `};

  ${({ small }) =>
    small &&
    css`
      height: 30px;
      padding: 6px 12px;
    `}
`;

const Input = styled.input<{ red?: boolean }>`
  width: 100%;
  height: 100%;
  color: ${({ red }) => (red ? COLORS.error.active : COLORS.black["100%"])};
  font-size: 14px;
  line-height: 20px;

  &::placeholder {
    color: ${COLORS.black["20%"]};
  }
`;

export default InputText;
